import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Pencil, Plus, Trash2, FileDown, XCircle, Ban, Check, X } from "lucide-react";
import {
  useUpdatePO, useDeletePO, usePOLineItems, useCreatePOLine, useUpdatePOLine, useDeletePOLine,
} from "@/hooks/usePurchaseOrders";
import { useParts, usePartVendors } from "@/hooks/useParts";
import { useVendorContacts } from "@/hooks/useVendorContacts";
import { VendorSearchInput } from "@/components/shared/VendorSearchInput";
import { useOrganization } from "@/hooks/useOrganization";
import { printPO } from "@/lib/printPO";
import type { PurchaseOrder, POStatus } from "@/hooks/usePurchaseOrders";

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-gray-100 text-gray-800",
  submitted: "bg-blue-100 text-blue-800",
  sent: "bg-indigo-100 text-indigo-800",
  partial: "bg-yellow-100 text-yellow-800",
  complete: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

interface PODetailSheetProps {
  po: PurchaseOrder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PODetailSheet({ po: poProp, open, onOpenChange }: PODetailSheetProps) {
  const [displayPO, setDisplayPO] = useState<PurchaseOrder | null>(null);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState<string>("");
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [editVendorId, setEditVendorId] = useState("");
  const [editShipTo, setEditShipTo] = useState("");
  const [editBillTo, setEditBillTo] = useState("");

  // Line item add form
  const [showAddLine, setShowAddLine] = useState(false);
  const [linePartId, setLinePartId] = useState("");
  const [linePartNumber, setLinePartNumber] = useState("");
  const [lineVendorPartNumber, setLineVendorPartNumber] = useState("");
  const [lineDesc, setLineDesc] = useState("");
  const [lineQty, setLineQty] = useState("1");
  const [lineUnitCost, setLineUnitCost] = useState("");

  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [editLineDesc, setEditLineDesc] = useState("");
  const [editLineQty, setEditLineQty] = useState("");
  const [editLineUnitCost, setEditLineUnitCost] = useState("");

  const updatePO = useUpdatePO();
  const deletePO = useDeletePO();
  const createLine = useCreatePOLine();
  const updateLine = useUpdatePOLine();
  const deleteLine = useDeletePOLine();
  const { data: lineItems = [] } = usePOLineItems(displayPO?.id || null);
  const { data: parts = [] } = useParts();
  const { data: vendors = [] } = useVendorContacts();
  const { data: linePartVendors = [] } = usePartVendors(linePartId || null);
  const { data: org } = useOrganization();

  const handlePrint = async () => {
    if (!displayPO) return;
    const vendor = vendors.find((v) => v.id === displayPO.vendor_id) ?? null;
    await printPO(displayPO, lineItems, vendor, org ? { name: org.name, logo_url: org.logo_url } : null, parts, displayPO.vendor_id ?? null);
  };

  const handleCancel = () => {
    if (!displayPO) return;
    updatePO.mutate(
      { id: displayPO.id, input: { status: "cancelled" } },
      { onSuccess: (data) => { setDisplayPO(data); setShowCancelDialog(false); } }
    );
  };

  const handleDelete = () => {
    if (!displayPO) return;
    deletePO.mutate(displayPO.id, { onSuccess: () => onOpenChange(false) });
  };

  useEffect(() => {
    setDisplayPO(poProp);
    setIsEditingStatus(false);
    setIsEditingHeader(false);
    setShowAddLine(false);
  }, [poProp]);

  const orgAddress = (org as any)?.address ?? "";

  const getPartNumber = (partId?: string | null): string => {
    if (!partId) return "—";
    return parts.find((p) => p.id === partId)?.part_number || "—";
  };

  const handleEditHeader = () => {
    if (!displayPO) return;
    setEditVendorId(displayPO.vendor_id ?? "");
    setEditShipTo(displayPO.ship_to ?? orgAddress);
    setEditBillTo(displayPO.bill_to ?? orgAddress);
    setIsEditingHeader(true);
  };

  const editVendor = vendors.find((v) => v.id === editVendorId);

  const handleHeaderSave = () => {
    if (!displayPO) return;
    updatePO.mutate(
      { id: displayPO.id, input: { vendor_id: editVendorId || null, ship_to: editShipTo, bill_to: editBillTo } },
      { onSuccess: (data) => { setDisplayPO(data); setIsEditingHeader(false); } }
    );
  };

  const handleStatusSave = () => {
    if (!displayPO || !newStatus) return;
    updatePO.mutate(
      { id: displayPO.id, input: { status: newStatus as POStatus } },
      {
        onSuccess: (data) => {
          setDisplayPO(data);
          setIsEditingStatus(false);
        },
      }
    );
  };

  const handleAddLine = () => {
    if (!displayPO || !lineDesc) return;
    createLine.mutate(
      {
        po_id: displayPO.id,
        part_id: linePartId || undefined,
        description: lineDesc,
        quantity: parseFloat(lineQty) || 1,
        unit_cost: parseFloat(lineUnitCost) || 0,
      },
      {
        onSuccess: () => {
          setLinePartId(""); setLineDesc(""); setLineQty("1"); setLineUnitCost("");
          setShowAddLine(false);
        },
      }
    );
  };

  const handlePartSelect = (partId: string) => {
    setLinePartId(partId);
    setLineVendorPartNumber("");
    const part = parts.find((p) => p.id === partId);
    if (part) {
      setLinePartNumber(part.part_number || "");
      setLineDesc(part.name);
      if (part.unit_cost) setLineUnitCost(part.unit_cost.toString());
    } else {
      setLinePartNumber("");
    }
    // Vendor part # will be resolved via linePartVendors once the query updates
  };

  if (!displayPO) return null;

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[560px] sm:max-w-[560px] overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle className="text-xl font-mono">{displayPO.po_number}</SheetTitle>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={handlePrint}
              disabled={editingLineId !== null}
              title={editingLineId !== null ? "Save or cancel the line item edit before printing" : undefined}
            >
              <FileDown className="h-4 w-4" />PDF
            </Button>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={STATUS_COLORS[displayPO.status] || ""}>{displayPO.status}</Badge>
            {!isEditingStatus ? (
              <Button variant="ghost" size="sm" onClick={() => { setNewStatus(displayPO.status); setIsEditingStatus(true); }}>
                <Pencil className="h-3 w-3" />
              </Button>
            ) : (
              <div className="flex items-center gap-1">
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(["draft", "submitted", "sent", "partial", "complete", "cancelled"] as const).map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={handleStatusSave}>Save</Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setIsEditingStatus(false)}>Cancel</Button>
              </div>
            )}
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-6">

          {/* Vendor + Addresses */}
          {!isEditingHeader ? (
            <div className="space-y-3">
              <div className="flex items-start justify-between">
                <div className="space-y-3 flex-1">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Vendor</p>
                    <p className="text-sm font-semibold">
                      {displayPO.vendor_id
                        ? vendors.find((v) => v.id === displayPO.vendor_id)?.company ?? "—"
                        : <span className="text-muted-foreground italic">No vendor selected</span>}
                    </p>
                    {displayPO.vendor_id && vendors.find((v) => v.id === displayPO.vendor_id)?.address && (
                      <p className="text-xs text-muted-foreground whitespace-pre-line mt-0.5">
                        {vendors.find((v) => v.id === displayPO.vendor_id)?.address}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Ship To</p>
                      <p className="text-xs whitespace-pre-line">{displayPO.ship_to || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Bill To</p>
                      <p className="text-xs whitespace-pre-line">{displayPO.bill_to || "—"}</p>
                    </div>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="ml-2 shrink-0" onClick={handleEditHeader}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 p-3 border rounded-lg bg-muted/20">
              <div className="space-y-1.5">
                <Label className="text-xs">Vendor</Label>
                {editVendor ? (
                  <div className="flex items-center gap-2 h-8 px-3 rounded-md border bg-muted/40 text-sm">
                    <span className="flex-1 truncate">{editVendor.company}</span>
                    <button type="button" onClick={() => setEditVendorId("")} className="text-muted-foreground hover:text-foreground">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <VendorSearchInput vendors={vendors} onSelect={(v) => setEditVendorId(v.id)} placeholder="Search vendors…" className="h-8 text-sm" />
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Ship To</Label>
                  <textarea
                    value={editShipTo}
                    onChange={(e) => setEditShipTo(e.target.value)}
                    rows={3}
                    className="w-full text-xs bg-background border rounded-md px-2 py-1.5 resize-none"
                    placeholder="Shipping address…"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Bill To</Label>
                  <textarea
                    value={editBillTo}
                    onChange={(e) => setEditBillTo(e.target.value)}
                    rows={3}
                    className="w-full text-xs bg-background border rounded-md px-2 py-1.5 resize-none"
                    placeholder="Billing address…"
                  />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setIsEditingHeader(false)}>Cancel</Button>
                <Button size="sm" className="h-7 text-xs" onClick={handleHeaderSave} disabled={updatePO.isPending}>Save</Button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Order Date</p>
              <p>{displayPO.order_date || "—"}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Expected</p>
              <p>{displayPO.expected_date || "—"}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Received</p>
              <p>{displayPO.received_date || "—"}</p>
            </div>
          </div>

          <Separator />

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium">Line Items ({lineItems.length})</p>
              <Button variant="outline" size="sm" onClick={() => setShowAddLine(!showAddLine)}>
                <Plus className="h-3 w-3 mr-1" />{showAddLine ? "Cancel" : "Add Line"}
              </Button>
            </div>

            {showAddLine && (
              <div className="p-3 border rounded-lg mb-3 space-y-3">
                <div className="space-y-2">
                  <Label className="text-xs">Part (optional)</Label>
                  <Select value={linePartId} onValueChange={handlePartSelect}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select part..." /></SelectTrigger>
                    <SelectContent>
                      {parts.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}{p.part_number ? ` (${p.part_number})` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {linePartNumber && (
                  <div className="space-y-2">
                    <Label className="text-xs">OEM Part #</Label>
                    <Input className="h-8 text-xs font-mono" value={linePartNumber} readOnly />
                  </div>
                )}
                {linePartId && (() => {
                  const vpn = linePartVendors.find((pv: any) => pv.vendor_id === displayPO?.vendor_id)?.vendor_part_number;
                  return vpn ? (
                    <div className="space-y-2">
                      <Label className="text-xs">Vendor Part #</Label>
                      <Input className="h-8 text-xs font-mono" value={vpn} readOnly />
                    </div>
                  ) : null;
                })()}
                <div className="space-y-2">
                  <Label className="text-xs">Description *</Label>
                  <Input className="h-8 text-xs" value={lineDesc} onChange={(e) => setLineDesc(e.target.value)} required />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Qty</Label>
                    <Input className="h-8 text-xs" type="number" value={lineQty} onChange={(e) => setLineQty(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Unit Cost ($)</Label>
                    <Input className="h-8 text-xs" type="number" step="0.01" value={lineUnitCost} onChange={(e) => setLineUnitCost(e.target.value)} />
                  </div>
                </div>
                <Button size="sm" onClick={handleAddLine} disabled={!lineDesc || createLine.isPending}>
                  {createLine.isPending ? "Adding..." : "Add"}
                </Button>
              </div>
            )}

            {lineItems.length > 0 ? (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Description</TableHead>
                      <TableHead className="text-xs text-center">OEM Part #</TableHead>
                      <TableHead className="text-xs text-right">Qty</TableHead>
                      <TableHead className="text-xs text-right">Unit $</TableHead>
                      <TableHead className="text-xs text-right">Total</TableHead>
                      <TableHead className="text-xs w-8" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lineItems.map((li) => (
                      <TableRow key={li.id}>
                        {editingLineId === li.id ? (
                          <>
                            <TableCell className="text-xs p-1">
                              <Input className="h-7 text-xs" value={editLineDesc} onChange={(e) => setEditLineDesc(e.target.value)} />
                            </TableCell>
                            <TableCell className="text-xs p-1 text-muted-foreground text-center">
                              {getPartNumber(li.part_id)}
                            </TableCell>
                            <TableCell className="text-xs p-1">
                              <Input className="h-7 text-xs w-16 text-right" type="number" value={editLineQty} onChange={(e) => setEditLineQty(e.target.value)} />
                            </TableCell>
                            <TableCell className="text-xs p-1">
                              <Input className="h-7 text-xs w-20 text-right" type="number" step="0.01" value={editLineUnitCost} onChange={(e) => setEditLineUnitCost(e.target.value)} />
                            </TableCell>
                            <TableCell className="text-xs text-right text-muted-foreground">
                              ${((parseFloat(editLineQty) || 0) * (parseFloat(editLineUnitCost) || 0)).toFixed(2)}
                            </TableCell>
                            <TableCell className="p-1">
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => {
                                  updateLine.mutate({ id: li.id, poId: displayPO.id, input: { description: editLineDesc, quantity: parseFloat(editLineQty) || 1, unit_cost: parseFloat(editLineUnitCost) || 0 } }, { onSuccess: () => setEditingLineId(null) });
                                }}>
                                  <Check className="h-3 w-3 text-primary" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setEditingLineId(null)}>
                                  <X className="h-3 w-3 text-muted-foreground" />
                                </Button>
                              </div>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell className="text-xs">{li.description}</TableCell>
                            <TableCell className="text-xs text-center">{getPartNumber(li.part_id)}</TableCell>
                            <TableCell className="text-xs text-right">{li.quantity}</TableCell>
                            <TableCell className="text-xs text-right">${li.unit_cost.toFixed(2)}</TableCell>
                            <TableCell className="text-xs text-right font-medium">${li.line_total.toFixed(2)}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {displayPO.status === "draft" && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    onClick={() => {
                                      setEditingLineId(li.id);
                                      setEditLineDesc(li.description);
                                      setEditLineQty(li.quantity.toString());
                                      setEditLineUnitCost(li.unit_cost.toString());
                                    }}
                                  >
                                    <Pencil className="h-3 w-3 text-muted-foreground" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() => deleteLine.mutate({ id: li.id, poId: displayPO.id })}
                                >
                                  <Trash2 className="h-3 w-3 text-muted-foreground" />
                                </Button>
                              </div>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No line items yet</p>
            )}
          </div>

          <Separator />

          {/* Totals */}
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span>${displayPO.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tax</span>
              <span>${displayPO.tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Shipping</span>
              <span>${displayPO.shipping.toFixed(2)}</span>
            </div>
            <Separator />
            <div className="flex justify-between font-semibold">
              <span>Total</span>
              <span>${displayPO.total.toFixed(2)}</span>
            </div>
          </div>

          {displayPO.notes && (
            <>
              <Separator />
              <div>
                <p className="text-sm font-medium mb-1">Notes</p>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{displayPO.notes}</p>
              </div>
            </>
          )}
          <Separator />
          <div className="flex items-center gap-2">
            {displayPO.status !== "cancelled" && (
              <Button variant="outline" size="sm" className="gap-1.5 text-muted-foreground" onClick={() => setShowCancelDialog(true)}>
                <Ban className="h-4 w-4" />Cancel PO
              </Button>
            )}
            <Button variant="outline" size="sm" className="gap-1.5 text-destructive hover:text-destructive ml-auto" onClick={() => setShowDeleteDialog(true)}>
              <Trash2 className="h-4 w-4" />Delete
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>

    {/* Cancel confirm */}
    <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel {displayPO.po_number}?</AlertDialogTitle>
          <AlertDialogDescription>
            This will mark the PO as cancelled. It will remain visible in your records.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep PO</AlertDialogCancel>
          <AlertDialogAction onClick={handleCancel}>Cancel PO</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Delete confirm */}
    <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {displayPO.po_number}?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently remove the purchase order and cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep PO</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={handleDelete}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
